import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import {
  Container,
  SelectContainer,
  SelectInput,
  SearchInput,
  OptionsContainer,
  Option,
  SelectedValue,
  IconContainer,
  NoOptions,
} from './styles';

export interface SelectOption {
  value: string | number;
  label: string;
}

interface Select2Props {
  options: SelectOption[];
  placeholder?: string;
  value?: SelectOption | null;
  onChange?: (option: SelectOption) => void;
  disabled?: boolean;
  className?: string;
  maxVisibleOptions?: number;
  id?: string;
  name?: string;
}

const Select2: React.FC<Select2Props> = ({
  options,
  placeholder = 'Selecione uma opção',
  value = null,
  onChange,
  disabled = false,
  className = '',
  maxVisibleOptions = 3,
  id,
  name,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>(options);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<Array<HTMLDivElement | null>>([]);
  const listboxId = useRef<string>(`select2-listbox-${id || Math.random().toString(36).substr(2, 9)}`);

  // Limita o número de opções visíveis
  const visibleOptions = filteredOptions.slice(0, maxVisibleOptions);

  // Redefine as refs para as opções quando elas são filtradas
  useEffect(() => {
    optionsRef.current = Array(visibleOptions.length).fill(null);
  }, [visibleOptions]);

  useEffect(() => {
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);

    // Resetar o índice destacado quando as opções filtradas mudam
    if (filtered.length > 0) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Rolar para a opção destacada quando o índice muda
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && optionsRef.current[highlightedIndex]) {
      optionsRef.current[highlightedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [highlightedIndex, isOpen]);

  const handleToggleDropdown = () => {
    if (!disabled) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);

      if (newIsOpen) {
        if (value) {
          setSearchTerm(value.label);

          // Encontrar e destacar a opção correspondente ao valor
          const selectedIndex = options.findIndex(option => option.value === value.value);

          if (selectedIndex >= 0 && selectedIndex < visibleOptions.length) {
            setHighlightedIndex(selectedIndex);
          } else {
            setHighlightedIndex(0);
          }
        } else {
          setSearchTerm('');
          setHighlightedIndex(0);
        }

        if (inputRef.current) {
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
              inputRef.current.select();
            }
          }, 50);
        }
      } else {
        setHighlightedIndex(-1);
      }
    }
  };

  const handleOptionClick = (option: SelectOption) => {
    if (onChange) {
      onChange(option);
    }
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);

    // Focar no input após selecionar uma opção
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);

    // Abrir o dropdown quando o usuário começa a digitar
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (!isOpen) {
      setIsOpen(true);

      // Se um valor já está selecionado, inicializar o searchTerm com o label do valor
      if (value) {
        setSearchTerm(value.label);

        // Encontrar e destacar a opção correspondente ao valor
        const selectedIndex = options.findIndex(option => option.value === value.value);

        if (selectedIndex >= 0 && selectedIndex < visibleOptions.length) {
          setHighlightedIndex(selectedIndex);
        } else {
          setHighlightedIndex(0);
        }

        // Selecionar todo o texto
        if (inputRef.current) {
          setTimeout(() => {
            inputRef.current?.select();
          }, 10);
        }
      }
    }
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();

    // Se o input já está focado, selecionamos o texto
    if (document.activeElement === inputRef.current) {
      inputRef.current?.select();
    }

    // Se o dropdown não estiver aberto, abrimos
    if (!isOpen) {
      handleInputFocus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Ignorar eventos que já foram tratados no input
    if (inputRef.current === document.activeElement) {
      return;
    }

    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (visibleOptions.length > 0) {
          setHighlightedIndex((prevIndex) => {
            const nextIndex = prevIndex < visibleOptions.length - 1 ? prevIndex + 1 : 0;
            return nextIndex;
          });
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (visibleOptions.length > 0) {
          setHighlightedIndex((prevIndex) => {
            const nextIndex = prevIndex > 0 ? prevIndex - 1 : visibleOptions.length - 1;
            return nextIndex;
          });
        }
        break;
      case 'Home':
        e.preventDefault();
        if (visibleOptions.length > 0) {
          setHighlightedIndex(0);
        }
        break;
      case 'End':
        e.preventDefault();
        if (visibleOptions.length > 0) {
          setHighlightedIndex(visibleOptions.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (highlightedIndex >= 0 && visibleOptions[highlightedIndex]) {
          handleOptionClick(visibleOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        if (inputRef.current) {
          inputRef.current.focus();
        }
        break;
      case 'Tab':
        // Deixar o Tab navegar naturalmente, mas fechar o dropdown
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        // Caso a tecla seja uma letra ou número, manter o comportamento padrão
        // para permitir a digitação na busca
        break;
    }
  };

  // Manipulador específico para o campo de pesquisa
  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Este handler só trata eventos do input
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault(); // Impedir o comportamento padrão (mover o cursor para o final)
        if (visibleOptions.length > 0) {
          setHighlightedIndex((prevIndex) => {
            const nextIndex = prevIndex < visibleOptions.length - 1 ? prevIndex + 1 : 0;
            return nextIndex;
          });
        }
        break;
      case 'ArrowUp':
        e.preventDefault(); // Impedir o comportamento padrão (mover o cursor para o início)
        if (visibleOptions.length > 0) {
          setHighlightedIndex((prevIndex) => {
            const nextIndex = prevIndex > 0 ? prevIndex - 1 : visibleOptions.length - 1;
            return nextIndex;
          });
        }
        break;
      case 'Enter':
        if (highlightedIndex >= 0 && visibleOptions[highlightedIndex]) {
          e.preventDefault();
          handleOptionClick(visibleOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        // Deixar o Tab navegar naturalmente
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        // Não fazer nada para permitir a digitação
        break;
    }

    // Não propagar o evento para o container para evitar processamento duplo
    e.stopPropagation();
  };

  const setOptionRef = (el: HTMLDivElement | null, index: number) => {
    optionsRef.current[index] = el;
  };

  const handleSelectedValueClick = () => {
    if (!disabled) {
      setIsOpen(true);
      if (value) {
        setSearchTerm(value.label);

        // Encontrar e destacar a opção correspondente ao valor
        const selectedIndex = filteredOptions.findIndex(option => option.value === value.value);
        if (selectedIndex >= 0) {
          setHighlightedIndex(selectedIndex);
        }

        // Focar e selecionar o texto do input
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
          }
        }, 50);
      }
    }
  };

  return (
    <Container
      className={className}
      ref={containerRef}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <SelectContainer>
        <SelectInput
          onClick={handleToggleDropdown}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-owns={listboxId.current}
          role="combobox"
          tabIndex={0}
        >
          {!isOpen && value ? (
            <SelectedValue onClick={handleSelectedValueClick}>{value.label}</SelectedValue>
          ) : (
            <SearchInput
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onFocus={handleInputFocus}
              onClick={handleInputClick}
              placeholder={placeholder}
              disabled={disabled}
              aria-autocomplete="list"
              aria-controls={listboxId.current}
              id={id}
              name={name}
            />
          )}
          <IconContainer>
            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
          </IconContainer>
        </SelectInput>

        <OptionsContainer
          isOpen={isOpen}
          role="listbox"
          id={listboxId.current}
          aria-label={placeholder}
        >
          {visibleOptions.length > 0 ? (
            visibleOptions.map((option, index) => (
              <Option
                key={option.value}
                ref={(el) => setOptionRef(el, index)}
                isSelected={value?.value === option.value}
                isHighlighted={index === highlightedIndex}
                onClick={() => handleOptionClick(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                role="option"
                aria-selected={value?.value === option.value}
                id={`${listboxId.current}-option-${index}`}
                tabIndex={-1}
              >
                {option.label}
              </Option>
            ))
          ) : (
            <NoOptions>Nenhuma opção encontrada</NoOptions>
          )}
        </OptionsContainer>
      </SelectContainer>
    </Container>
  );
};

export default Select2;
